import { z } from 'zod';

// ─── Question complexity ──────────────────────────────────────────────────────

export const questionComplexitySchema = z.enum(['LOW', 'MEDIUM', 'HIGH']);
export type QuestionComplexity = z.infer<typeof questionComplexitySchema>;

// ─── Generated question (unified superset for real flow + Test Lab) ───────────

export const generatedQuestionSchema = z.object({
  question: z.string(),
  inputType: z.enum(['voice', 'text', 'both']).default('voice'),
  complexity: questionComplexitySchema.default('MEDIUM'),
  // Per-stage metadata (optional; forms a shared superset across paths)
  expectedDepth: z.enum(['short', 'medium', 'deep']).optional(),
  scenarioType: z.enum(['text', 'code', 'none']).optional(),
  scenarioContent: z.string().optional(),
  pressureType: z.enum(['silence', 'tough', 'negotiation']).optional(),
  skillFocus: z.enum(['vocabulary', 'grammar', 'fluency']).optional(),
  reason: z.string().optional(),
});
export type GeneratedQuestion = z.infer<typeof generatedQuestionSchema>;

// ─── Report payload (stage + final, 0-100 scoring) ───────────────────────────

export const reportPayloadSchema = z.object({
  strengths: z.array(z.string()).default([]),
  improvements: z.array(z.string()).default([]),
  languageNotes: z.string().nullable().default(null),
  contentScore: z.number().min(0).max(100).nullable().default(null),
  communicationScore: z.number().min(0).max(100).nullable().default(null),
  languageScore: z.number().min(0).max(100).nullable().default(null),
  toneAssessment: z.string().nullable().default(null),
  characterAssessment: z.string().nullable().default(null),
  verdict: z.string().default(''),
  overallScore: z.number().min(0).max(100).nullable().default(null),
  // Generated only by the final report prompt
  standoutQuestions: z
    .array(z.object({ question: z.string(), highlight: z.string() }))
    .nullable()
    .default(null),
});
export type ReportPayload = z.infer<typeof reportPayloadSchema>;

// ─── Language assessment payload (CEFR + structured) ─────────────────────────

export const languageErrorSchema = z.object({
  type: z.enum(['grammar', 'vocabulary', 'syntax', 'pronunciation', 'register']),
  quote: z.string(),
  fix: z.string(),
  severity: z.enum(['minor', 'moderate', 'major']).default('moderate'),
});
export type LanguageError = z.infer<typeof languageErrorSchema>;

export const languageRoleReadinessSchema = z.object({
  verdict: z.enum(['sufficient', 'risky', 'insufficient']),
  risk: z.enum(['low', 'medium', 'high']),
  note: z.string(),
});
export type LanguageRoleReadiness = z.infer<typeof languageRoleReadinessSchema>;

export const languageScenarioSchema = z.object({
  scenario: z.string(),
  verdict: z.enum(['ok', 'risky', 'blocker']),
  note: z.string(),
});
export type LanguageScenario = z.infer<typeof languageScenarioSchema>;

/**
 * Why the answers could not produce a trustworthy CEFR level. `no_answers` and
 * `too_short` come from a deterministic check on the submitted text; `off_topic`,
 * `gibberish` and `wrong_language` can only be judged by the evaluator model.
 */
export const languageUnassessableReasonSchema = z.enum([
  'no_answers',
  'too_short',
  'off_topic',
  'gibberish',
  'wrong_language',
]);
export type LanguageUnassessableReason = z.infer<typeof languageUnassessableReasonSchema>;

export const languageAssessmentSchema = z.object({
  level: z.enum(['A1', 'A2', 'B1', 'B2', 'C1', 'C2']),
  cefrEstimate: z.string().optional(),
  errors: z.array(languageErrorSchema).default([]),
  roleReadiness: languageRoleReadinessSchema,
  scenarios: z.array(languageScenarioSchema).default([]),
  summary: z.string(),
  assessable: z.boolean().default(true),
  unassessableReason: languageUnassessableReasonSchema.nullable().default(null),
});
export type LanguageAssessmentPayload = z.infer<typeof languageAssessmentSchema>;

// ─── CEFR level type (mirrors Prisma enum after migration) ───────────────────

export const cefrLevelSchema = z.enum(['A1', 'A2', 'B1', 'B2', 'C1', 'C2']);
export type CefrLevel = z.infer<typeof cefrLevelSchema>;
